import React from 'react';
import { cn } from '../../../../lib/utils';
import type { TaskItem } from '../../../../types/topic';
import { TaskCard } from '../TaskCard';
import { TaskForm } from '../TaskForm';
import { logger } from '../../../../utils/logger';
import { useTaskColumns } from '../../../../hooks/queries/useTaskColumns';
import { useTaskUIStore } from '../../../../store/taskUIStore';
import { extractBaseColor, getStatusColor } from '../../../../utils/colorUtils';
import { EmptyTaskState } from '../EmptyTaskState';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { useViewport } from '../../../../contexts/ViewportContext.tsx';

interface MobileTaskColumnProps {
  id: TaskItem['status'];
  items: TaskItem[];
  isCreating: boolean;
  activeColumn: TaskItem['status'];
  title: string;
  description: string;
  showDescription: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  itemToDelete: string | null;
  onDelete: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onCancelDelete: () => void;
  onUpdateTask: (id: string, updates: Partial<TaskItem>) => void;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  // Filter props
  showUnscheduledOnly?: boolean;
  showUnassignedOnly?: boolean;
  userFilters?: UserFilter[];
  // Collaboration flag for assigning tasks
  hasCollaborators?: boolean;
  // Available tags for task form
  availableTags?: string[];
  // Task reordering callback
  onReorderTask: (taskId: string, direction: 'up' | 'down' | 'top') => Promise<void>;
}

export function MobileTaskColumn({
  id,
  items,
  isCreating,
  activeColumn,
  title,
  description,
  showDescription,
  inputRef,
  itemToDelete,
  onDelete,
  onDeleteClick,
  onCancelDelete,
  onUpdateTask,
  onTitleChange,
  onDescriptionChange,
  onSubmit,
  onCancel,
  showUnscheduledOnly,
  showUnassignedOnly,
  userFilters,
  hasCollaborators = false,
  availableTags = [],
  onReorderTask,
}: MobileTaskColumnProps) {
  // Get columns from React Query hook
  const { data: columns = [] } = useTaskColumns(items.length > 0 ? items[0].topic_id : null);
  const { isMobile } = useViewport();

  // Get task state and actions
  const {
    newTaskTags,
    newTaskDeadline,
    newTaskAssignedTo,
    setNewTaskTags,
    setNewTaskDeadline,
    setNewTaskAssignedTo,
  } = useTaskUIStore();
  // Available tags are now passed as prop from parent

  return (
    <div className="p-4 min-h-full">
      <div className="space-y-3">
        {/* Add task form with left border styling */}
        {isCreating && activeColumn === id && (
          <div
            className={cn(
              'bg-white shadow p-4 rounded-[5px] mt-[7px]',
              isMobile && 'py-2',
              'border-l-[5px]',
              (() => {
                // Format columns for consistent color calculations
                // This is the same approach as in TaskCard.tsx
                const formattedColumns = columns.map(col => ({
                  status: col.status,
                  color: extractBaseColor(col.color) || 'gray',
                }));

                // Use the getStatusColor utility for consistency
                // Use the same approach as other components
                return getStatusColor(id, formattedColumns, 'border', false);
              })()
            )}
          >
            <TaskForm
              isEditMode={false}
              title={title}
              description={description}
              showDescription={showDescription}
              onTitleChange={onTitleChange}
              onDescriptionChange={onDescriptionChange}
              onSubmitNewTask={() => {
                onSubmit();
              }}
              onCancel={onCancel}
              inputRef={inputRef}
              status={id}
              tags={newTaskTags}
              availableTags={availableTags}
              onTagsChange={setNewTaskTags}
              deadline={newTaskDeadline}
              assignedTo={newTaskAssignedTo}
              onAssigneeChange={setNewTaskAssignedTo}
              hasCollaborators={hasCollaborators}
              onDeadlineChange={date => {
                if (date) {
                  const newDate = new Date(date.getTime());
                  newDate.setHours(12, 0, 0, 0);
                  setNewTaskDeadline(newDate);
                } else {
                  setNewTaskDeadline(null);
                }
              }}
            />
          </div>
        )}

        {items.length === 0 ? (
          // Show empty state when there are no tasks
          <EmptyTaskState
            showUnscheduledOnly={showUnscheduledOnly}
            showUnassignedOnly={showUnassignedOnly}
            userFilters={userFilters}
            isMobile={true}
          />
        ) : (
          // Render tasks when available
          items.map((item, index) => (
            <TaskCard
              key={item.id}
              task={item}
              isFirst={index === 0}
              isLast={index === items.length - 1}
              itemToDelete={itemToDelete}
              onDelete={onDelete}
              onDeleteClick={onDeleteClick}
              onCancelDelete={onCancelDelete}
              onUpdateTask={onUpdateTask}
              hasCollaborators={hasCollaborators}
              onMoveTask={async (taskId, direction) => {
                logger.component('Moving task in column', { taskId, direction });
                await onReorderTask(taskId, direction);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}
